import { useField } from 'formik';
import { Suspense, useEffect } from 'react';
import { graphql, useQueryLoader } from 'react-relay';

import usePaginatorSearchOverlayQueryLoader from '../../../hooks/usePaginatorSearchOverlayQueryLoader';
import {
  UnitCPaginatorSearchOverlayTokenField_unitCsQuery,
  UnitCPaginatorSearchOverlayTokenField_unitCsQuery$variables,
} from '../../../relay/__generated__/UnitCPaginatorSearchOverlayTokenField_unitCsQuery.graphql';
import { isNullable } from '../../../utils/is';
import Checkbox from '../../core/Checkbox';
import EmptyState from '../../core/EmptyState';
import Grid from '../../core/Grid';
import ItemList from '../../core/ItemList';
import PreloadedQueryRenderer from '../../core/PreloadedQueryRenderer';
import ScrollContainer from '../../core/ScrollContainer';
import SearchOverlayTokenField, { SearchOverlayTokenFieldProps } from '../../core/SearchOverlayTokenField';
import Spinner from '../../core/Spinner';
import Text from '../../core/Text';
import View from '../../core/View';
import UnitCPaginator from '../UnitCPaginator';

const unitCsForUnitCPaginatorSearchOverlayTokenField = graphql`
  query UnitCPaginatorSearchOverlayTokenField_unitCsQuery(
    $filters: UnitCFilter
    $order: UnitCOrder
    $first: Int
    $after: String
  ) {
    unitCs(filters: $filters, order: $order, first: $first, after: $after) {
      edges {
        node {
          id
          title
        }
      }
    }
    ...UnitCPaginator_query @arguments(filters: $filters, order: $order, first: $first, after: $after)
  }
`;

type Props = {
  variables?: UnitCPaginatorSearchOverlayTokenField_unitCsQuery$variables;
} & Omit<SearchOverlayTokenFieldProps, 'renderItemList'>;

const UnitCPaginatorSearchOverlayTokenField = ({ name = '', value: propValue, variables, ...props }: Props) => {
  const [{ value: baseValue }] = useField<string[] | undefined>({ name });
  const value = !isNullable(propValue) ? propValue : baseValue;

  const [valueQueryReference, loadValueQuery, disposeValueQuery] =
    useQueryLoader<UnitCPaginatorSearchOverlayTokenField_unitCsQuery>(unitCsForUnitCPaginatorSearchOverlayTokenField);
  useEffect(() => {
    loadValueQuery({ filters: { id_In: value || [] } });
    return () => disposeValueQuery();
  }, [baseValue, loadValueQuery, disposeValueQuery]);

  const [searchOverlayFieldProps, queryReference] =
    usePaginatorSearchOverlayQueryLoader<UnitCPaginatorSearchOverlayTokenField_unitCsQuery>(
      unitCsForUnitCPaginatorSearchOverlayTokenField,
      variables,
    );

  return valueQueryReference ? (
    <PreloadedQueryRenderer query={unitCsForUnitCPaginatorSearchOverlayTokenField} queryReference={valueQueryReference}>
      {({ unitCs }) => (
        <SearchOverlayTokenField
          name={name}
          value={value}
          renderTokenText={(id) => unitCs?.edges.find(({ node }) => node.id === id)?.node.title || ''}
          renderItemList={({ containerRef, handleSelect }, itemProps) =>
            queryReference ? (
              <Suspense fallback={<Spinner sx={{ paddingY: 3 }} />}>
                <PreloadedQueryRenderer
                  query={unitCsForUnitCPaginatorSearchOverlayTokenField}
                  queryReference={queryReference}
                >
                  {(queryData) => (
                    <UnitCPaginator fragmentReference={queryData}>
                      {(data, { hasNext, isLoadingNext, loadMore }) => (
                        <ScrollContainer
                          ref={containerRef as React.RefObject<HTMLDivElement>}
                          onScrollReachEnd={() => {
                            if (hasNext && !isLoadingNext) loadMore(20);
                          }}
                        >
                          <ItemList
                            items={data.unitCs.edges}
                            renderItem={({ node: { title } }) => <Text sx={{ fontSize: 1 }}>{title}</Text>}
                            renderItemWrapper={(children, { node: { id } }) => (
                              <View
                                {...itemProps}
                                key={id}
                                onClick={(e) => handleSelect(e, id)}
                                onKeyDown={(e) => handleSelect(e, id)}
                              >
                                <Grid gapX={2} wrap={false}>
                                  <Grid.Unit size={'min'}>
                                    <Checkbox checked={value?.some((selectedId) => selectedId === id)} />
                                  </Grid.Unit>
                                  <Grid.Unit size={'max'}>{children}</Grid.Unit>
                                </Grid>
                              </View>
                            )}
                            emptyState={
                              <View sx={{ paddingY: 3 }}>
                                <EmptyState
                                  title={'검색 결과가 없어요'}
                                  description={'다른 검색어로 다시 시도해주세요.'}
                                />
                              </View>
                            }
                          />
                          <View>{isLoadingNext ? <Spinner size={'small'} /> : null}</View>
                        </ScrollContainer>
                      )}
                    </UnitCPaginator>
                  )}
                </PreloadedQueryRenderer>
              </Suspense>
            ) : null
          }
          {...searchOverlayFieldProps}
          {...props}
        />
      )}
    </PreloadedQueryRenderer>
  ) : (
    <Spinner size={'small'} />
  );
};

export default UnitCPaginatorSearchOverlayTokenField;
